"use client";

import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import React, { useEffect, useState } from "react";
import { ActionGroup } from "@/components/ActionGroup";
import Icon from "@/components/Icon";
import { PlusGrid, PlusGridItem, PlusGridRow } from "@/components/PlusGrid";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useNavbar } from "@/context/NavbarContext";
import { cn } from "@/lib/utils";
import type { NavbarProps } from "./Navbar";

const MobileNavbar = ({ logoUrl, items, actions }: NavbarProps) => {
	const [open, setOpen] = useState(false);
	const pathname = usePathname();
	const { scrolled } = useNavbar();
	useEffect(() => {
		const handleResize = () => {
			if (window.innerWidth > 1024) {
				setOpen(false);
			}
		};

		window.addEventListener("resize", handleResize);

		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, []);

	return (
		<Sheet open={open} onOpenChange={setOpen}>
			{/* Header with conditional backdrop blur based on scroll */}
			<header
				className={cn(
					"fixed inset-x-0 top-0 z-999 border-b transition-all duration-200 xl:hidden",
					{
						"border-black/5 bg-white": scrolled,
						"border-transparent pt-6 sm:pt-8": !scrolled,
					},
				)}
			>
				<PlusGrid className="relative px-4">
					<PlusGridRow className="relative flex items-center justify-between">
						<PlusGridItem className="py-3">
							<Link href="/">
								<Image
									src={logoUrl}
									alt="Memorang"
									width={1024}
									height={512}
									className="h-10 w-fit min-w-32 max-w-36"
								/>
							</Link>
						</PlusGridItem>

						<PlusGridItem className="py-3">
							<SheetTrigger asChild>
								<Button variant="ghost" size="icon" className="text-foreground">
									<Icon name="Menu" />
								</Button>
							</SheetTrigger>
						</PlusGridItem>
					</PlusGridRow>
				</PlusGrid>
			</header>

			<aside>
				<nav>
					<SheetContent
						side="right"
						className="z-9999 flex flex-col gap-y-4 border-none bg-white p-4 px-0"
					>
						<div className="flex h-full flex-col gap-y-4 rounded-xl bg-white p-4">
							<PlusGrid className="relative">
								<PlusGridRow className="relative">
									<PlusGridItem className="py-3">
										<Link href="/" onClick={() => setOpen(false)}>
											<Image
												src={logoUrl}
												alt="Memorang"
												width={1024}
												height={512}
												className="h-10 w-fit min-w-32 max-w-36"
											/>
										</Link>
									</PlusGridItem>
								</PlusGridRow>
							</PlusGrid>

							<ScrollArea className="flex flex-1 flex-col p-2">
								{items.map((item, index) => {
									if (item.type === "flyout") {
										return (
											<div
												key={`mobile-${item.name}-${index}`}
												className="my-6 first:mt-2"
											>
												<h1 className="font-semibold text-base text-gray-900">
													{item.name}
												</h1>
												<div className="mt-3 flex flex-col space-y-3">
													{item.items.map((listItem, listIndex) => {
														let itemIcon: React.ReactElement | null = null;

														if (listItem.image) {
															itemIcon = (
																<Image
																	src={listItem.image}
																	alt={listItem.name}
																	width={128}
																	height={128}
																	className="h-6 w-6 object-contain"
																/>
															);
														} else if (listItem.icon) {
															itemIcon = (
																<div className="flex h-6 w-6 items-center justify-center">
																	<Icon
																		name={listItem.icon}
																		className="w-5 text-primary"
																	/>
																</div>
															);
														}

														return (
															<Link
																key={`mobile-flyout-${listItem.name}-${listIndex}`}
																href={listItem.href}
																target={
																	listItem.external ? "_blank" : undefined
																}
																rel={
																	listItem.external
																		? "noopener noreferrer"
																		: undefined
																}
																className={cn(
																	"flex items-center gap-x-3 rounded-md py-1 transition-colors",
																	{
																		"font-medium text-primary":
																			pathname.includes(listItem.href),
																		"text-gray-600 hover:text-gray-900":
																			!pathname.includes(listItem.href),
																	},
																)}
															>
																{itemIcon}
																<span className="font-medium text-sm">
																	{listItem.name}
																</span>
															</Link>
														);
													})}
												</div>
											</div>
										);
									}

									return (
										<React.Fragment key={`mobile-link-${item.name}-${index}`}>
											{index !== 0 && (
												<Separator className="my-4 bg-black/10" />
											)}
											<Link href={item.href} onClick={() => setOpen(false)}>
												<div
													className={cn(
														"rounded-md px-1 py-2 font-medium text-sm transition-colors",
														{
															"text-primary": pathname.includes(item.href),
															"text-gray-600 hover:text-gray-900":
																!pathname.includes(item.href),
														},
													)}
												>
													{item.name}
												</div>
											</Link>
										</React.Fragment>
									);
								})}
							</ScrollArea>

							<ActionGroup
								actions={actions}
								className="mt-4 flex flex-wrap justify-start px-4"
							/>
						</div>
					</SheetContent>
				</nav>
			</aside>
		</Sheet>
	);
};

export default MobileNavbar;
